import { useEffect, useRef } from 'react'
import { useFormikContext, FormikErrors } from 'formik'

export type ChangeCallback<Values> = (values: Values, errors: FormikErrors<Values>) => void
export type ErrorCallback<Values> = (errors: FormikErrors<Values>) => void
export type ValidCallback = () => void

export interface FormikObserverProps<Values> {
  autoSubmit?: boolean
  onChange?: ChangeCallback<Values>
  onError?: ErrorCallback<Values>
  onValid?: ValidCallback
}

export function FormikObserver<Values> ({ autoSubmit, onChange, onError, onValid }: FormikObserverProps<Values>) {
  const { values, submitForm, isValid, errors, dirty } = useFormikContext<Values>()
  const hasErrors = Boolean(Object.keys(errors).length)
  const firstLoad = useRef(true)
  const customDirty = useRef(false)
  const onChangeCallback = useRef<ChangeCallback<Values> | undefined>(onChange).current
  const onErrorCallback = useRef<ErrorCallback<Values> | undefined>(onError).current
  const onValidCallback = useRef<ValidCallback | undefined>(onValid).current

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false
    } else {
      customDirty.current = true
    }
    if (!onChangeCallback) {
      return
    }

    onChangeCallback?.(values, errors)
  }, [values, errors, onChangeCallback])

  useEffect(() => {
    if (!autoSubmit) {
      return
    }

    if (!hasErrors && (dirty || customDirty.current) && isValid) {
      submitForm()
    }
  }, [values, submitForm, dirty, isValid, autoSubmit, hasErrors])

  useEffect(() => {
    if (hasErrors && onErrorCallback) {
      onErrorCallback(errors)
    }
  }, [errors, hasErrors, onErrorCallback])

  useEffect(() => {
    if (dirty && !hasErrors && onValidCallback) {
      onValidCallback()
    }
  }, [dirty, hasErrors, onValidCallback])

  return null
}
